import { t as translate } from 'i18next'
import { GridColumns } from '@mui/x-data-grid-pro'
import { useCallback, useEffect, useMemo } from 'react'
import { CenteredSpinner } from '../../../components/Spinner'
import { IMainComponent } from '../../../generated-types/main-component'
import { useGetMainComponentsList } from '../../../api/main-component'
import { useFormContext } from 'react-hook-form'
import { Box } from '@mui/material'
import GenericTable from 'src/components/table/GenericTable'
import { renderCaptionedCell } from 'src/components/table/CaptionedCell'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import NoDeviationStepperContent from '../../../assets/svg/no-deviation-stepper-content.svg'
import { getTranslatedText } from 'src/utils'

export interface ISelectMainComponentStepProps {
  customerId: EntityId
  setDisabled: React.Dispatch<React.SetStateAction<boolean>>
  handleNext: () => void
}

interface IGenericMainComponentSelectStep {
  setDisabled: React.Dispatch<React.SetStateAction<boolean>>
  handleNext: () => void
  mainComponents: IMainComponent[]
  isLoading: boolean
}

export const GenericMainComponentSelectStep: React.FC<IGenericMainComponentSelectStep> = ({
  setDisabled,
  handleNext,
  mainComponents,
  isLoading,
}) => {
  const { getValues, reset } = useFormContext()

  useEffect(() => {
    if (getValues('mainComponent')?.id === undefined) {
      setDisabled(true)
    } else {
      setDisabled(false)
    }
  }, [getValues('mainComponent'), setDisabled])

  const STATIC_COLUMNS: GridColumns = useMemo(
    () => [
      {
        field: 'description',
        headerName: translate('name', { ns: 'common' }),
        flex: 1,
        minWidth: 320,
        cellClassName: 'font-weight-semi-bold',
        renderCell: ({ row }) =>
          renderCaptionedCell(
            row.description || getTranslatedText(row.type.name),
            `${getTranslatedText(row.type.name)} | ${row.serialNumber}`,
          ),
      },
    ],
    [],
  )

  const handleRowClick = useCallback(
    params => {
      reset({
        ...getValues(),
        mainComponent: params.row as IMainComponent,
      })
      setDisabled(false)
      handleNext()
    },
    [setDisabled, handleNext],
  )

  if (isLoading) return <CenteredSpinner />

  return mainComponents.length !== 0 ? (
    <Box width={'100%'} sx={{ ml: '10px' }}>
      <GenericTable
        columns={STATIC_COLUMNS}
        data={mainComponents}
        height={window.innerHeight - 500}
        autoHeight={false}
        loading={isLoading}
        selectionModel={getValues('mainComponent') ? [getValues('mainComponent').id] : []}
        onRowClick={handleRowClick}
        enableQuickFilter={false}
        getRowHeight={() => 72}
        minRowHeight="72px !important"
        id={'main-component-step'}
      />
    </Box>
  ) : (
    <Box className="text-center">
      <EmptyStatusCard
        image={NoDeviationStepperContent}
        header={translate('deviation-component-empty-title', { ns: 'deviation' })}
        content={translate('deviation-component-empty-content', { ns: 'deviation' })}
        imageWidth="89px"
        headerFontSize="18px"
        contentFontSize="14px"
        contentClasses="mt-8"
        imageContainerClasses="p-4"
        contentTopMargin="10px"
      />
    </Box>
  )
}

export const SelectMainComponentStep: React.FC<ISelectMainComponentStepProps> = ({
  customerId,
  setDisabled,
  handleNext,
}) => {
  const { getValues } = useFormContext()

  const { data: components, isLoading } = useGetMainComponentsList(customerId, getValues('position')?.id as EntityId)

  return (
    <GenericMainComponentSelectStep
      mainComponents={components?.items as IMainComponent[]}
      isLoading={isLoading}
      setDisabled={setDisabled}
      handleNext={handleNext}
    />
  )
}
